// SalseQuotation.jsx
import React, { useState, useEffect, useMemo } from "react";
import { quotationService } from "../../service/Vendorservisefile/quotationaprovelService.jsx";
import "./Qutectionaprovel.css";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const statusMap = {
      'SUBMITTED': { label: 'Submitted', icon: '📤', color: 'status-badge-sent' },
      'APPROVED': { label: 'Approved', icon: '✓', color: 'status-badge-approved' },
      'REJECTED': { label: 'Rejected', icon: '✗', color: 'status-badge-rejected' },
      'PENDING': { label: 'Pending', icon: '⏳', color: 'status-badge-pending' },
      'PENDING_REVIEW': { label: 'Pending Review', icon: '⏳', color: 'status-badge-pending' },
      'DRAFT': { label: 'Draft', icon: '📝', color: 'status-badge-default' },
      'EXPIRED': { label: 'Expired', icon: '⌛', color: 'status-badge-rejected' },
    };
    return statusMap[status] || { label: status, icon: '•', color: 'status-badge-default' };
  };

  const config = getStatusConfig(status);
  return (
    <span className={`status-badge ${config.color}`}>
      <span>{config.icon}</span>
      {config.label}
    </span>
  );
};

// Admin Status Badge
const AdminStatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const statusMap = {
      'PENDING_REVIEW': { label: 'Pending Review', color: 'admin-status-pending' },
      'APPROVED': { label: 'Approved', color: 'admin-status-approved' },
      'REJECTED': { label: 'Rejected', color: 'admin-status-rejected' },
      'REVIEWED': { label: 'Reviewed', color: 'admin-status-reviewed' },
    };
    return statusMap[status] || { label: status, color: 'admin-status-default' };
  };

  const config = getStatusConfig(status);
  return <span className={`admin-status-badge ${config.color}`}>{config.label}</span>;
};

// Search and Filter Bar Component
const SearchFilterBar = ({ searchTerm, setSearchTerm, filter, setFilter, statusCounts }) => {
  return (
    <div className="search-filter-bar">
      <div className="search-wrapper">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search by ID, Project, or Client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm('')}>
            ✕
          </button>
        )}
      </div>
      
      <div className="filter-wrapper">
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses ({statusCounts.all || 0})</option>
          <option value="SUBMITTED">Submitted ({statusCounts.SUBMITTED || 0})</option>
          <option value="PENDING_REVIEW">Pending Review ({statusCounts.PENDING_REVIEW || 0})</option>
          <option value="APPROVED">Approved ({statusCounts.APPROVED || 0})</option>
          <option value="REJECTED">Rejected ({statusCounts.REJECTED || 0})</option>
          <option value="DRAFT">Draft ({statusCounts.DRAFT || 0})</option>
          <option value="EXPIRED">Expired ({statusCounts.EXPIRED || 0})</option>
        </select>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ label, count, icon, color, onClick, isActive }) => {
  return (
    <div 
      className={`stats-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="stats-card-header">
        <span className="stats-icon">{icon}</span>
        <span className="stats-label">{label}</span>
      </div>
      <div className="stats-count">{count}</div>
      <div className={`stats-bar ${color}`}></div>
    </div>
  );
};

// Document Preview Component
const DocumentPreview = ({ documents }) => {
  if (!documents || documents.length === 0) return null;
  
  return (
    <div className="document-preview">
      {documents.map((doc, index) => (
        <a 
          key={doc.id || index}
          href={doc.documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="document-link"
        >
          📄 {doc.documentName}
        </a>
      ))}
    </div>
  );
};

// Version History Component
const VersionHistory = ({ versions }) => {
  const [showVersions, setShowVersions] = useState(false);
  
  if (!versions || versions.length <= 1) return null;
  
  return (
    <div className="version-history">
      <button 
        className="version-toggle"
        onClick={() => setShowVersions(!showVersions)}
      >
        {showVersions ? '▼' : '▶'} {versions.length} Versions
      </button>
      {showVersions && (
        <div className="version-list">
          {versions.map((v, idx) => (
            <div key={idx} className="version-item">
              <span className="version-number">v{v.version || idx + 1}</span>
              <span className="version-amount">{formatCurrency(v.quotationAmount)}</span>
              <span className="version-status">
                <StatusBadge status={v.status} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Quotation Table Component
const QuotationTable = ({ quotations, loading, error }) => {
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p className="loading-text">Loading quotations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <h3 className="error-title">Error Loading Data</h3>
          <p className="error-message">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!quotations || quotations.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📭</div>
        <p className="empty-text">No quotations found</p>
        <p className="empty-subtext">Try adjusting your filters or search criteria</p>
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      <table className="quotation-table">
        <thead className="table-header">
          <tr>
            <th>Quotation ID</th>
            <th>Enquiry ID</th>
            <th>Client</th>
            <th className="hide-mobile">Amount</th>
            <th className="hide-tablet">Valid Until</th>
            <th className="hide-mobile">Delivery</th>
            <th>Status</th>
            <th className="hide-tablet">Admin Status</th>
            {/* <th className="hide-desktop">Details</th> */}
          </tr>
        </thead>
        <tbody>
          {quotations.map((q, index) => (
            <tr 
              key={q.quotationId} 
              className={`table-row ${index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}`}
            >
              <td className="table-cell-id">
                <span className="quotation-id">#{q.quotationId}</span>
                {q.version > 1 && <span className="version-badge">v{q.version}</span>}
              </td>
              <td className="table-cell-enquiry">
                <span className="enquiry-id">#ENQ-{q.enquiryId}</span>
              </td>
              <td className="table-cell-client">
                <div className="client-info">
                  <span className="client-name">{q.clientName || 'N/A'}</span>
                </div>
              </td>
              <td className="table-cell-amount hide-mobile">
                <span className="amount-value">{formatCurrency(q.quotationAmount)}</span>
              </td>
              <td className="table-cell-validity hide-tablet">
                <span className="validity-date">{formatDate(q.validUntil)}</span>
              </td>
              <td className="table-cell-delivery hide-mobile">
                <span className="delivery-days">{q.estimatedDeliveryDays || 'N/A'} days</span>
              </td>
              <td className="table-cell-status">
                <StatusBadge status={q.quotationStatus} />
              </td>
              <td className="table-cell-admin-status hide-tablet">
                <AdminStatusBadge status={q.adminStatus} />
              </td>
              {/* <td className="table-cell-details hide-desktop">
                <button className="details-toggle">View Details</button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Main Component
const SalseQuotation = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch quotations from API
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get vendor ID from localStorage or context
        const vendorId = localStorage.getItem('vendorId') || 1;
        const response = await quotationService.getVendorQuotations(vendorId);
        
        // Sort quotations by quotationId (newest first)
        const sortedQuotations = response.sort((a, b) => b.quotationId - a.quotationId);
        setQuotations(sortedQuotations);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Failed to fetch quotations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, []);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = {
      all: quotations.length,
      SUBMITTED: 0,
      PENDING_REVIEW: 0,
      APPROVED: 0,
      REJECTED: 0,
      DRAFT: 0,
      EXPIRED: 0
    };
    
    quotations.forEach(q => {
      const status = q.quotationStatus || q.adminStatus;
      if (counts.hasOwnProperty(status)) {
        counts[status] = (counts[status] || 0) + 1;
      }
    });
    
    return counts;
  }, [quotations]);

  // Filter and search logic
  const filteredQuotations = useMemo(() => {
    return quotations.filter((q) => {
      // Filter by status
      const matchesFilter = filter === 'all' || 
        q.quotationStatus === filter || 
        q.adminStatus === filter;
      
      // Search by ID, client name, or enquiry ID
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        String(q.quotationId).toLowerCase().includes(searchLower) ||
        (q.clientName && q.clientName.toLowerCase().includes(searchLower)) ||
        String(q.enquiryId).toLowerCase().includes(searchLower);
      
      return matchesFilter && matchesSearch;
    });
  }, [quotations, filter, searchTerm]);

  // Stats cards configuration
  const statsConfig = [
    { key: 'all', label: 'Total', icon: '📊', color: 'stats-bar-all' },
    { key: 'SUBMITTED', label: 'Submitted', icon: '📤', color: 'stats-bar-sent' },
    { key: 'PENDING_REVIEW', label: 'Pending Review', icon: '⏳', color: 'stats-bar-pending' },
    { key: 'APPROVED', label: 'Approved', icon: '✅', color: 'stats-bar-approved' },
    { key: 'REJECTED', label: 'Rejected', icon: '❌', color: 'stats-bar-rejected' },
  ];

  return (
    <div className="quotation-container">
      <div className="quotation-wrapper">
        
        {/* Stats Cards */}
        <div className="stats-grid">
          {statsConfig.map((stat) => (
            <StatsCard
              key={stat.key}
              label={stat.label}
              count={statusCounts[stat.key] || 0}
              icon={stat.icon}
              color={stat.color}
              isActive={filter === stat.key}
              onClick={() => setFilter(stat.key)}
            />
          ))}
        </div>

        {/* Search and Filter Bar */}
        <SearchFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filter={filter}
          setFilter={setFilter}
          statusCounts={statusCounts}
        />

        {/* Results Count */}
        <div className="results-info">
          <span className="results-count">
            Showing {filteredQuotations.length} of {quotations.length} quotations
          </span>
          {filter !== 'all' && (
            <button 
              className="clear-filter"
              onClick={() => setFilter('all')}
            >
              Clear Filter ✕
            </button>
          )}
        </div>

        {/* Quotation Table */}
        <QuotationTable 
          quotations={filteredQuotations}
          loading={loading}
          error={error}
        />

        {/* Footer */}
        <div className="quotation-footer">
          <p className="footer-text">
            Last updated: {new Date().toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SalseQuotation;